import { IsDisabledContext, useIsDisabled } from '@ff-it/form';
import { NativeButtonProps } from '@ff-it/ui';
import { AddButton, RemoveButton, RemoveButtonProps } from 'components/ui';
import { Fragment, ReactElement, ReactNode } from 'react';
import { useForm } from 'react-final-form';
import { useFieldArray } from 'react-final-form-arrays';
import invariant from 'tiny-invariant';

export type InlineRenderProps<V = any> = {
  name: string;
  index: number;
  disabled: boolean;
  value: V;
};

type InlineProps = {
  name: string;
  //   initialValues?: T;
  //   min?: number;
  //   testId?: string;
  disabled?: boolean;
  children: (props: InlineRenderProps) => ReactNode;
  getKey?: (v: any, name: string, index: number) => string;
};

const defaultGetKey = (v: any, name: string, index: number) => {
  const key = v.id ?? v.__NEW__;
  invariant(key !== undefined, `failed to get key for ${name} index: ${index}`);
  return String(key);
};

const defaultValue: any[] = [];

export function Inlines({
  name,
  disabled: localDisabled,
  children,
  getKey = defaultGetKey,
}: InlineProps): ReactElement {
  const { fields } = useFieldArray<any>(name, {
    // FIXME: this is busted but we need it for DND keys, and to pass down int ocolumns
    subscription: { value: true },
    /*
    TODO: should default to [] - does not actually work.
    For some reason field array does not handle parse and to fix remove we would have to overload whole remove mutator
    */
    defaultValue,
  });
  const disabled = useIsDisabled(localDisabled);
  return (
    <IsDisabledContext.Provider value={disabled}>
      {fields.map((name, index) => {
        const value = fields.value?.[index];
        if (value === undefined) return null;
        const key = getKey(value, name, index);
        return <Fragment key={key}>{children({ name, index, disabled, value })}</Fragment>;
      })}
    </IsDisabledContext.Provider>
  );
}

type AddInlineProps = {
  name: string;
  initialValues: any;
  testId?: string;
  children?: ReactNode;
  size?: NativeButtonProps['size'];
  className?: string;
};

export function AddInline({
  name,
  initialValues,
  testId = 'add-inline',
  children = 'Add',
  ...btnProps
}: AddInlineProps): ReactElement {
  const {
    mutators: { push },
    getFieldState,
  } = useForm();

  const isDisabled = useIsDisabled();
  const addClickHandler = () => {
    push(
      name,
      typeof initialValues === 'function'
        ? initialValues((getFieldState(name)?.value?.length || 0) + 1)
        : {
            ...initialValues,
            __NEW__: Date.now(),
          },
    );
  };

  return (
    <AddButton testId={testId} onClick={addClickHandler} disabled={isDisabled} {...btnProps}>
      {children}
    </AddButton>
  );
}

type RemoveInlineProps = {
  name: string;
  index: number;
  min?: number;
} & RemoveButtonProps;

export function RemoveInline({
  name,
  index,
  min,
  testId = 'remove-inline',
  ...btnProps
}: RemoveInlineProps): ReactElement {
  const {
    mutators: { remove },
  } = useForm();

  const isDisabled = useIsDisabled();
  const removeClickHandler = () => {
    remove(name, index);
  };

  return (
    <RemoveButton
      testId={testId}
      onClick={removeClickHandler}
      disabled={isDisabled || Boolean(min && min > index)}
      {...btnProps}
    />
  );
}
